import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Scale } from 'lucide-react';
import { PlayIcon } from '@heroicons/react/24/outline';
import FileUploadZone from './FileUploadZone';
import UploadedFileChip from './UploadedFileChip';
import { config } from '../../../../config';

interface NewSimulationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  signalId?: string; // Optional - required when starting from a specific signal
}

const NewSimulationDialog: React.FC<NewSimulationDialogProps> = ({
  open,
  onOpenChange,
  signalId,
}) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFilesAdded = (newFiles: File[]) => {
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRunSimulation = async () => {
    // Validate that we have either a signal ID or uploaded files
    if (!signalId && files.length === 0) {
      alert('Please select a signal or upload case files to start case guidance.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Create simulation via backend API
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${config.API_URL}/v1/ombudsman-decisions/simulations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          signal_id: signalId,
          metadata: files.length > 0 ? { files_uploaded: files.length } : null,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create case guidance');
      }

      const simulation = await response.json();

      // Navigate to case workspace with real simulation ID
      navigate(`/v3/workspace/ombudsman/${simulation.id}`);

      // Reset state
      setFiles([]);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create case guidance:', error);
      alert(`Failed to create case guidance: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Scale className="w-6 h-6 text-primary" />
            Start New Case
          </DialogTitle>
          <DialogDescription className="text-base leading-relaxed pt-2">
            Upload case evidence to get guidance on how the Financial Ombudsman Service might
            reason about this complaint. The Case Guidance will generate a suggested outcome,
            reasoning, redress guidance, and precedents.
          </DialogDescription>
        </DialogHeader>

        {/* File Upload Zone */}
        <div className="py-4">
          <FileUploadZone onFilesAdded={handleFilesAdded} />

          {/* Uploaded Files List */}
          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-foreground">
                Uploaded Files ({files.length})
              </p>
              <div className="space-y-2">
                {files.map((file, index) => (
                  <UploadedFileChip
                    key={index}
                    file={file}
                    onRemove={() => handleRemoveFile(index)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-muted-foreground text-center pb-2">
          Guidance based on published Ombudsman decisions and FCA DISP. Not a final decision.
        </p>

        <DialogFooter className="flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRunSimulation}
            disabled={isSubmitting || (!signalId && files.length === 0)}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <>
                <PlayIcon className="w-4 h-4 mr-2" />
                Get Guidance
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewSimulationDialog;
